import React, { useState, useEffect } from "react";
import "../styles/facturaReutilizable.css";
import { BiXCircle, BiUpload, BiRefresh, BiCheckDouble, BiTrash, BiArrowBack } from "react-icons/bi";
import { show_alerta } from "../functions";
import Axios from "axios";
import { Spinner } from "react-bootstrap";

const FacturaXMLEditable = ({ storageKey = "factura_progress", recepcionData = null, onVolver = null }) => {
    const [facturaData, setFacturaData] = useState(null);
    const [partidas, setPartidas] = useState([]);
    const [almacenes, setAlmacenes] = useState([]);
    const [almacenSeleccionado, setAlmacenSeleccionado] = useState("");
    const [claveProvSistema, setClaveProvSistema] = useState(null);
    const [loading, setLoading] = useState(false);
    const [recargandoMasivo, setRecargandoMasivo] = useState(false);
    const [filasCargando, setFilasCargando] = useState({});

    const urlServidorAPI = process.env.REACT_APP_URL_API1;

    // Valores fijos según MODELO_149486.mod
    const CONSTANTES = {
        ESQUEMA: 1,
        NUM_MONED: 1,
        CONDICION: "CONTADO"
    };

    // Cargar datos guardados al iniciar o desde recepcionData
    useEffect(() => {
        if (recepcionData) {
            const header = {
                id: recepcionData.id,
                proveedor: recepcionData.nombre_proveedor,
                rfc: recepcionData.rfc_proveedor,
                folio: recepcionData.folio_factura,
                fecha: (recepcionData.fecha_factura || "").split("T")[0]
            };
            setFacturaData(header);
            setAlmacenSeleccionado(String(recepcionData.almacen_sae || ""));

            const items = (recepcionData.partidas || []).map((p, index) => {
                const canti = parseFloat(p.cantidad_facturada || 0);
                const valorUnit = parseFloat(p.precio_unitario || 0);
                return {
                    id: p.id || index,
                    descripcion: p.descripcion || "Sin descripción",
                    cantidad: canti,
                    cantidad_facturada: parseFloat(p.cantidad_facturada || 0),
                    cantidad_recibida: parseFloat(p.cantidad_recibida || 0),
                    valorUnitario: valorUnit,
                    importe: canti * valorUnit,
                    claveProveedor: p.clave_proveedor,
                    claveInterna: p.clave_interna && p.clave_interna !== "No-registrada" ? p.clave_interna : null,
                    origen: null,
                    descuento: parseFloat(p.descuento_porcentaje || 0),
                    observacion: p.observacion
                };
            });
            setPartidas(items);

            if (header.rfc === "ROSA7504083F6" || header.rfc === "SAHR520527U29") {
                setClaveProvSistema(header.rfc === "ROSA7504083F6" ? "8" : "46");
            } else if (header.rfc === "SRS080522T77") {
                setClaveProvSistema("35");
                getClavesMasivas(header.rfc, items);
            } else {
                getClavesMasivas(header.rfc, items);
            }
            fetchAlmacenes();
            return;
        }

        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.facturaData) {
                    setFacturaData(parsed.facturaData);
                    setPartidas(parsed.partidas || []);
                    setAlmacenSeleccionado(parsed.almacenSeleccionado || "");
                    setClaveProvSistema(parsed.claveProvSistema || null);
                }
            } catch (e) {
                console.error("Error al cargar progreso guardado:", e);
            }
        }
        fetchAlmacenes();
    }, [storageKey, recepcionData]);

    // Guardar datos automáticamente ante cualquier cambio (solo si no es recepcionData)
    useEffect(() => {
        if (facturaData && !recepcionData) {
            const dataToSave = {
                facturaData,
                partidas,
                almacenSeleccionado,
                claveProvSistema
            };
            localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        }
    }, [facturaData, partidas, almacenSeleccionado, claveProvSistema, storageKey, recepcionData]);

    const fetchAlmacenes = () => {
        Axios.get(`${urlServidorAPI}/compras/getalmacenes`)
            .then(res => setAlmacenes(res.data))
            .catch(err => console.error("Error al cargar almacenes", err));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    };

    const getClavesMasivas = (rfc, items) => {
        setRecargandoMasivo(true);
        const clavesUnicas = [...new Set(items.map(p => p.claveProveedor))];
        Axios.post(`${urlServidorAPI}/compras/getclavesproveedor`, { rfc, claves: clavesUnicas })
            .then((response) => {
                const { cve_clpv, partidas: clavesServidor } = response.data;
                const claveFinalProv = rfc === "SRS080522T77" ? "35" : (cve_clpv || "NO ENCONTRADO");
                setClaveProvSistema(claveFinalProv);
                const partidasActualizadas = items.map(item => {
                    const coincidencia = (clavesServidor || response.data).find(c => 
                        c.claveprove?.trim().toUpperCase() === item.claveProveedor?.trim().toUpperCase()
                    );
                    const claveFinal = (coincidencia && coincidencia.clave) ? coincidencia.clave : "No-registrada";
                    return { ...item, claveInterna: claveFinal, origen: coincidencia?.origen || null };
                });
                setPartidas(partidasActualizadas);
                setRecargandoMasivo(false);
            })
            .catch(() => {
                setPartidas(items.map(it => ({ ...it, claveInterna: "No-registrada" })));
                setRecargandoMasivo(false);
            });
    };

    const getClaveUnitaria = (item) => {
        setFilasCargando(prev => ({ ...prev, [item.id]: true }));
        Axios.get(`${urlServidorAPI}/compras/getclavesproveedor`, {
            params: { rfc: facturaData.rfc, clave: item.claveProveedor, _t: Date.now() }
        }).then((response) => {
            const data = response.data;
            const coincidencia = Array.isArray(data) ? data[0] : data;
            setPartidas(prev => prev.map(p => {
                if (p.id === item.id) {
                    const claveFinal = (coincidencia && coincidencia.clave) ? coincidencia.clave : "No-registrada";
                    return { ...p, claveInterna: claveFinal, origen: coincidencia?.origen || null };
                }
                return p;
            }));
            setFilasCargando(prev => ({ ...prev, [item.id]: false }));
        }).catch(() => setFilasCargando(prev => ({ ...prev, [item.id]: false })));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        const reader = new FileReader();
        reader.onload = (event) => parseXML(event.target.result);
        reader.readAsText(file);
    };

    const parseXML = (xmlText) => {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");
            const ns4 = "http://www.sat.gob.mx/cfd/4";
            const ns3 = "http://www.sat.gob.mx/cfd/3";
            let emisorNode = xmlDoc.getElementsByTagNameNS(ns4, "Emisor")[0] || xmlDoc.getElementsByTagNameNS(ns3, "Emisor")[0];
            let comprobanteNode = xmlDoc.getElementsByTagNameNS(ns4, "Comprobante")[0] || xmlDoc.getElementsByTagNameNS(ns3, "Comprobante")[0];
            let conceptosNodes = xmlDoc.getElementsByTagNameNS(ns4, "Concepto") || xmlDoc.getElementsByTagNameNS(ns3, "Concepto");

            const header = {
                proveedor: emisorNode?.getAttribute("Nombre") || "N/A",
                rfc: emisorNode?.getAttribute("Rfc") || "N/A",
                folio: comprobanteNode.getAttribute("Folio") || "S/F",
                fecha: (comprobanteNode.getAttribute("Fecha") || "S/F").split('T')[0]
            };

            const items = [];
            for (let i = 0; i < conceptosNodes.length; i++) {
                const node = conceptosNodes[i];
                items.push({
                    id: i,
                    descripcion: node.getAttribute("Descripcion") || "Sin descripción",
                    cantidad: parseFloat(node.getAttribute("Cantidad") || 0),
                    valorUnitario: parseFloat(node.getAttribute("ValorUnitario") || 0),
                    importe: parseFloat(node.getAttribute("Importe") || 0),
                    claveProveedor: node.getAttribute("NoIdentificacion") || "N/A",
                    claveInterna: null,
                    origen: null,
                    descuento: 0
                });
            }
            setFacturaData(header);
            setLoading(false);
            if (header.rfc === "ROSA7504083F6" || header.rfc === "SAHR520527U29") {
                const claveSistema = header.rfc === "ROSA7504083F6" ? "8" : "46";
                setClaveProvSistema(claveSistema);
                setPartidas(items.map(item => ({
                    ...item,
                    claveInterna: item.claveProveedor,
                    origen: null
                })));
            } else if (header.rfc === "SRS080522T77") {
                setClaveProvSistema("35");
                getClavesMasivas(header.rfc, items);
            } else {
                getClavesMasivas(header.rfc, items);
            }
        } catch (error) {
            setLoading(false);
        }
    };

    const handleDescuentoChange = (id, value) => {
        setPartidas(partidas.map(item => item.id === id ? { ...item, descuento: parseFloat(value) || 0 } : item));
    };

    const eliminarPartida = (id) => {
        setPartidas(prev => prev.filter(p => p.id !== id));
    };

    const calcularTotales = () => {
        const subtotal = partidas.reduce((acc, item) => acc + item.importe, 0);
        const descuentoTotal = partidas.reduce((acc, item) => acc + ((item.importe * item.descuento) / 100), 0);
        const baseGravable = subtotal - descuentoTotal;
        const iva = baseGravable * 0.16;
        const total = baseGravable + iva;
        return { subtotal, descuentoTotal, iva, total };
    };

    const { subtotal, descuentoTotal, iva, total } = calcularTotales();
    const tienePendientes = partidas.some(p => p.claveInterna === "No-registrada" || !p.claveInterna);
    const faltaAlmacen = !almacenSeleccionado;

    // Extraer únicamente las clasificaciones elegidas por el operador para incidencias
    const clasificacionesIncidencia = Array.from(new Set(
        (recepcionData?.partidas || [])
            .filter(p => p.observacion || (p.estatus_partida && !['PENDIENTE', 'COMPLETA'].includes(p.estatus_partida)))
            .map(p => {
                const match = (p.observacion || '').match(/^\[(.*?)\]/);
                if (match && match[1]) return match[1].trim();
                if (p.tipoAnomalia) return p.tipoAnomalia.trim();
                if (p.estatus_partida === 'DANADA') return 'Dañado';
                if (p.estatus_partida === 'INCOMPLETA') return 'Faltante';
                if (p.estatus_partida === 'INCORRECTO') return 'Incorrecto';
                if (p.estatus_partida === 'OTRA') return 'Otra';
                if (p.estatus_partida === 'NO_RECIBIDA') return 'No Recibida';
                return 'Incidencia';
            })
    ));

    if ((recepcionData?.tiene_incidencias === 1 || recepcionData?.estatus === 'RECIBIDO_CON_INCIDENCIA') && clasificacionesIncidencia.length === 0) {
        clasificacionesIncidencia.push("Incidencia reportada");
    }

    const reset = () => {
        localStorage.removeItem(storageKey); // Limpieza física del almacenamiento
        setFacturaData(null);
        setPartidas([]);
        setClaveProvSistema(null);
        setAlmacenSeleccionado("");
        setFilasCargando({});
    };

    const finalizarProceso = () => {
        try {
            const xmlMod = generarXMLMod();
            const blob = new Blob([xmlMod], { type: "text/xml" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `OC_${claveProvSistema}_${facturaData.folio}.mod`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            if (recepcionData?.id) {
                Axios.put(`${urlServidorAPI}/api/v1/recepcion/${recepcionData.id}/cerrar-oc`)
                    .then(() => {
                        show_alerta("Archivo .MOD generado y estatus actualizado en el sistema", "success");
                        if (onVolver) {
                            setTimeout(() => onVolver(), 1500);
                        }
                    })
                    .catch((err) => {
                        console.error("Error al actualizar estatus de OC en backend:", err);
                        show_alerta("Archivo .MOD generado con éxito", "success");
                    });
            } else {
                show_alerta("Archivo .MOD generado", "success");
            }
        } catch (error) {
            show_alerta("Error al generar archivo", "error");
        }
    };

    const generarXMLMod = () => {
        let xml = `<?xml version="1.0" standalone="yes"?>\n<DATAPACKET Version="2.0">\n<METADATA>\n<FIELDS>\n`;
        xml += `<FIELD attrname="CVE_CLPV" fieldtype="string" WIDTH="10"/>\n<FIELD attrname="NUM_ALMA" fieldtype="i4"/>\n<FIELD attrname="CVE_PEDI" fieldtype="string" WIDTH="20"/>\n<FIELD attrname="ESQUEMA" fieldtype="i4"/>\n<FIELD attrname="DES_TOT" fieldtype="r8"/>\n<FIELD attrname="DES_FIN" fieldtype="r8"/>\n<FIELD attrname="CVE_VEND" fieldtype="string" WIDTH="5"/>\n<FIELD attrname="COM_TOT" fieldtype="r8"/>\n<FIELD attrname="NUM_MONED" fieldtype="i4"/>\n<FIELD attrname="TIPCAMB" fieldtype="r8"/>\n<FIELD attrname="STR_OBS" fieldtype="string" WIDTH="255"/>\n<FIELD attrname="ENTREGA" fieldtype="string" WIDTH="25"/>\n<FIELD attrname="SU_REFER" fieldtype="string" WIDTH="20"/>\n<FIELD attrname="TOT_IND" fieldtype="r8"/>\n<FIELD attrname="MODULO" fieldtype="string" WIDTH="4"/>\n<FIELD attrname="CONDICION" fieldtype="string" WIDTH="25"/>\n<FIELD attrname="dtfield" fieldtype="nested">\n<FIELDS>\n<FIELD attrname="CANT" fieldtype="r8"/>\n<FIELD attrname="CVE_ART" fieldtype="string" WIDTH="20"/>\n<FIELD attrname="DESC1" fieldtype="r8"/>\n<FIELD attrname="DESC2" fieldtype="r8"/>\n<FIELD attrname="DESC3" fieldtype="r8"/>\n<FIELD attrname="IMPU1" fieldtype="r8"/>\n<FIELD attrname="IMPU2" fieldtype="r8"/>\n<FIELD attrname="IMPU3" fieldtype="r8"/>\n<FIELD attrname="IMPU4" fieldtype="r8"/>\n<FIELD attrname="COMI" fieldtype="r8"/>\n<FIELD attrname="PREC" fieldtype="r8"/>\n<FIELD attrname="NUM_ALM" fieldtype="i4"/>\n<FIELD attrname="STR_OBS" fieldtype="string" WIDTH="255"/>\n<FIELD attrname="REG_GPOPROD" fieldtype="i4"/>\n<FIELD attrname="REG_KITPROD" fieldtype="i4"/>\n<FIELD attrname="NUM_REG" fieldtype="i4"/>\n<FIELD attrname="COSTO" fieldtype="r8"/>\n<FIELD attrname="TIPO_PROD" fieldtype="string" WIDTH="1"/>\n<FIELD attrname="TIPO_ELEM" fieldtype="string" WIDTH="1"/>\n<FIELD attrname="MINDIRECTO" fieldtype="r8"/>\n<FIELD attrname="TIP_CAM" fieldtype="r8"/>\n<FIELD attrname="FACT_CONV" fieldtype="r8"/>\n<FIELD attrname="UNI_VENTA" fieldtype="string" WIDTH="10"/>\n<FIELD attrname="IMP1APLA" fieldtype="i4"/>\n<FIELD attrname="IMP2APLA" fieldtype="i4"/>\n<FIELD attrname="IMP3APLA" fieldtype="i4"/>\n<FIELD attrname="IMP4APLA" fieldtype="i4"/>\n<FIELD attrname="PREC_SINREDO" fieldtype="r8"/>\n<FIELD attrname="COST_SINREDO" fieldtype="r8"/>\n<FIELD attrname="LOTE" fieldtype="string" WIDTH="16"/>\n<FIELD attrname="PEDIMENTO" fieldtype="string" WIDTH="16"/>\n<FIELD attrname="FECHCADUC" fieldtype="dateTime"/>\n<FIELD attrname="FECHADUANA" fieldtype="dateTime"/>\n<FIELD attrname="CVE_PRODSERV" fieldtype="string" WIDTH="9"/>\n<FIELD attrname="CVE_UNIDAD" fieldtype="string" WIDTH="4"/>\n</FIELDS>\n<PARAMS/>\n</FIELD>\n</FIELDS>\n<PARAMS/>\n</METADATA>\n<ROWDATA>\n`;
        const valorSuRefer = String(claveProvSistema).trim() === "35" ? "" : facturaData.folio;
        xml += `<ROW CVE_CLPV="${claveProvSistema}" NUM_ALMA="${almacenSeleccionado}" ESQUEMA="${CONSTANTES.ESQUEMA}" DES_TOT="0" DES_FIN="0" NUM_MONED="${CONSTANTES.NUM_MONED}" TIPCAMB="1" STR_OBS="" ENTREGA="" SU_REFER="${valorSuRefer}" TOT_IND="0" MODULO="COMP">\n`;
        xml += `<dtfield>\n`;
        partidas.forEach((p, index) => {
            const factorDescuento = 1 - (p.descuento / 100);
            const precioNeto = (p.valorUnitario * factorDescuento).toFixed(6);
            xml += `<ROWdtfield CANT="${p.cantidad}" CVE_ART="${p.claveInterna}" DESC1="0" IMPU1="0" IMPU2="0" IMPU3="0" IMPU4="16" PREC="0" NUM_ALM="${almacenSeleccionado}" STR_OBS="" REG_GPOPROD="0" COSTO="${precioNeto}" TIPO_PROD="P" TIPO_ELEM="N" MINDIRECTO="0" TIP_CAM="1" FACT_CONV="1" UNI_VENTA="PZ" IMP1APLA="6" IMP2APLA="6" IMP3APLA="6" IMP4APLA="0" PREC_SINREDO="0" COST_SINREDO="${precioNeto}"/>\n`;
        });
        xml += `</dtfield>\n</ROW>\n</ROWDATA>\n</DATAPACKET>`;
        return xml;
    };

    if (!facturaData) {
        return (
            <div className="factura-reutilizable mt-0">
                <div className="header-factura" style={{ justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                    <h5 className="mb-0 py-2">Procesar Reposiciones</h5>
                </div>
                <div className="upload-section mt-3">
                    {loading ? (
                        <div className="py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Procesando archivo XML...</p>
                        </div>
                    ) : (
                        <>
                            <BiUpload size={50} color="#007bff" />
                            <h4>Cargar Factura XML</h4>
                            <p>Seleccione el archivo XML del proveedor para comenzar</p>
                            <input type="file" accept=".xml" className="form-control" onChange={handleFileUpload} style={{ maxWidth: "400px", margin: "20px auto" }} />
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="factura-reutilizable" style={{ position: "relative" }}>
            {recargandoMasivo && (
                <div style={{
                    position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                    backgroundColor: "rgba(255,255,255,0.7)", zIndex: 100,
                    display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"
                }}>
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 fw-bold">Buscando claves en el sistema...</p>
                </div>
            )}
            {(recepcionData?.tiene_incidencias === 1 || recepcionData?.estatus === 'RECIBIDO_CON_INCIDENCIA' || clasificacionesIncidencia.length > 0) && (
                <div className="banner-aviso-oc">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <span className="fw-bold text-danger">
                            ⚠️ Factura con incidencias reportadas en {recepcionData.sucursal_destino}:
                        </span>
                        <div className="d-flex align-items-center gap-1">
                            {clasificacionesIncidencia.map((clasif, idx) => (
                                <span 
                                    key={idx} 
                                    className="badge bg-danger px-2 py-1"
                                    style={{ fontSize: "0.78rem", fontWeight: "600" }}
                                >
                                    {clasif}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="header-factura">
                <div className="header-item">
                    <strong>Proveedor</strong>
                    <span>{facturaData.proveedor}</span>
                </div>
                <div className="header-item">
                    <strong>RFC</strong>
                    <span>{facturaData.rfc}</span>
                </div>
                <div className="header-item">
                    <strong>Clave PROV</strong>
                    <span className="badge-prov-sistema">{claveProvSistema || "..."}</span>
                </div>
                <div className="header-item">
                    <strong>Folio</strong>
                    <span>{facturaData.folio}</span>
                </div>
                <div className="header-item">
                    <strong>Fecha</strong>
                    <span>{facturaData.fecha}</span>
                </div>
                <div className="header-item" style={{ border: "none" }}>
                    <strong>Almacén</strong>
                    <select className="form-select form-select-sm" value={almacenSeleccionado} onChange={(e) => setAlmacenSeleccionado(e.target.value)} style={{ width: "130px" }}>
                        <option value="">Seleccione...</option>
                        {almacenes.map(alm => <option key={alm.id} value={alm.id}>{alm.id} - {alm.nombre}</option>)}
                    </select>
                </div>
            </div>

            <div className="tabla-factura-container">
                <table className="tabla-factura">
                    <thead>
                        <tr>
                            <th style={{ width: "5%" }}>Cant.</th>
                            <th style={{ width: "25%" }}>Clave Prov. / Descripción</th>
                            <th style={{ width: "15%" }}>Clave</th>
                            <th style={{ width: "9%", textAlign: "right" }}>V. Unitario</th>
                            <th style={{ width: "9%", textAlign: "right" }}>Importe</th>
                            <th style={{ width: "10%", textAlign: "right" }}>Desc. (%)</th>
                            <th style={{ width: "9%", textAlign: "right" }}>Monto Desc.</th>
                            <th style={{ width: "10%", textAlign: "right" }}>Subtotal</th>
                            <th style={{ width: "8%" }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {partidas.map((item) => {
                            const montoDesc = (item.importe * item.descuento) / 100;
                            const isNoReg = item.claveInterna === "No-registrada";
                            const loadingFila = filasCargando[item.id];
                            return (
                                <tr key={item.id}>
                                    <td>{item.cantidad}</td>
                                    <td>
                                        <strong>{item.claveProveedor}</strong>
                                        <span className="desc-secundaria">{item.descripcion}</span>
                                    </td>
                                    <td>
                                        <span className={`col-clave ${isNoReg ? "no-registrada" : ""}`}>
                                            {loadingFila ? "..." : (item.claveInterna || "...")}
                                        </span>
                                        {item.origen && !loadingFila && !isNoReg && <span className="origen-info">{item.origen}</span>}
                                        {isNoReg && (
                                            <button className="btn-refresh-min" onClick={() => getClaveUnitaria(item)} disabled={loadingFila}>
                                                <BiRefresh className={loadingFila ? "fa-spin" : ""} />
                                            </button>
                                        )}
                                    </td>
                                    <td style={{ textAlign: "right" }}>{formatCurrency(item.valorUnitario)}</td>
                                    <td style={{ textAlign: "right" }}>{formatCurrency(item.importe)}</td>
                                    <td style={{ textAlign: "right" }}>
                                        <div className="input-group input-group-sm" style={{ width: "85px", marginLeft: "auto" }}>
                                            <input type="number" className="form-control text-end" value={item.descuento} onChange={(e) => handleDescuentoChange(item.id, e.target.value)} min="0" max="100" step="0.1" />
                                            <span className="input-group-text">%</span>
                                        </div>
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {montoDesc.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                    </td>
                                    <td style={{ textAlign: "right" }}>{formatCurrency(item.importe - montoDesc)}</td>
                                    <td style={{ textAlign: "center" }}>
                                        <button className="btn btn-outline-danger btn-sm" onClick={() => eliminarPartida(item.id)} title="Eliminar fila">
                                            <BiTrash />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="footer-acciones">
                <div className="msg-validacion">
                    {tienePendientes || faltaAlmacen ? (
                        <div className="status-label status-alerta">
                           <span className="warning-icon">⚠️</span>
                           <span>{faltaAlmacen ? "Seleccione Almacén" : "Vincule todas las claves"}</span>
                        </div>
                    ) : (
                        <div className="status-label status-ok">
                           <span>✓ Listo para exportar</span>
                        </div>
                    )}
                </div>
                
                <div className="resumen-horizontal">
                    <div className="resumen-item">
                        <label>Subtotal</label>
                        <span>{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="resumen-item" style={{ color: "#d9534f" }}>
                        <label>Descuento</label>
                        <span>- {formatCurrency(descuentoTotal)}</span>
                    </div>
                    <div className="resumen-item">
                        <label>IVA (16%)</label>
                        <span>{formatCurrency(iva)}</span>
                    </div>
                    <div className="resumen-item total-destacado">
                        <label>Total Final</label>
                        <span>{formatCurrency(total)}</span>
                    </div>
                    <button className="btn btn-primary btn-finalizar-main" disabled={tienePendientes || faltaAlmacen} onClick={finalizarProceso}>
                        <BiCheckDouble size={20} /> Generar .MOD
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FacturaXMLEditable;



